import Admin from '../../Api/Models/Admin';
import { ROLE, RoleList, RoleType } from '../helpers/Permissions';

import Auth from './Auth';

/**
 * Any changes to this class should be reflected in app\Services\Crm\AdminPolicyService.php
 */
class AdminPolicyService {
    private readonly ROLES_ORDER: RoleList = [
        ROLE.SUPER_ADMIN,
        ROLE.WHITELABEL_SUPER_ADMIN,
        ROLE.ADMIN,
        ROLE.WHITELABEL_ADMIN,
        ROLE.MODERATOR,
        ROLE.OBSERVER,
        ROLE.SUPER_AGENT,
        ROLE.AFFILIATE_OWNER,
        ROLE.MASTER_AGENT,
        ROLE.MASTER_AFFILIATE_AGENT,
    ];

    public readonly SUPER_ROLES: RoleList = [ROLE.SUPER_ADMIN, ROLE.WHITELABEL_SUPER_ADMIN];
    public readonly NON_SUPER_ROLES: RoleList = [ROLE.ADMIN, ROLE.WHITELABEL_ADMIN];
    public readonly MAIN_ROLES: RoleList = [...this.SUPER_ROLES, ...this.NON_SUPER_ROLES];

    /**
     * Checks whether admin has a role from the SUPER_ROLES group
     */
    public hasSuperRole(admin: Admin): boolean {
        return Auth.userHasAnyRole(admin, this.SUPER_ROLES);
    }

    /**
     * Checks whether admin has a role from the MAIN_ROLES group
     */
    public hasMainRole(admin: Admin): boolean {
        return Auth.userHasAnyRole(admin, this.MAIN_ROLES);
    }

    /**
     * Checks whether admin can view targetAdmin
     */
    public adminCanView(admin: Admin, targetAdmin: Admin): boolean {
        if (admin.id === targetAdmin.id) {
            return true;
        }

        const role = this.getStrongestRole(admin);

        switch (true) {
            case this.SUPER_ROLES.includes(role):
                return true;

            case this.NON_SUPER_ROLES.includes(role):
                return !Auth.userHasAnyRole(targetAdmin, this.SUPER_ROLES);

            default:
                return false;
        }
    }

    /**
     * Checks whether admin can manage targetRole
     */
    public adminCanManage(admin: Admin, targetRole: RoleType | null): boolean {
        const role = this.getStrongestRole(admin);

        switch (true) {
            case role === ROLE.SUPER_ADMIN:
                return targetRole !== ROLE.SUPER_ADMIN;

            case role === ROLE.WHITELABEL_SUPER_ADMIN:
                return ![...this.SUPER_ROLES, ROLE.ADMIN].includes(targetRole);

            case this.NON_SUPER_ROLES.includes(role):
                return !this.MAIN_ROLES.includes(targetRole);

            default:
                return false;
        }
    }

    /**
     * Checks whether admin can modify targetAdmin
     */
    public adminCanModify(admin: Admin, targetAdmin: Admin): boolean {
        if (admin.id === targetAdmin.id && this.hasMainRole(admin)) {
            return true;
        }

        const role = this.getStrongestRole(admin);

        switch (true) {
            case role === ROLE.SUPER_ADMIN:
                return !Auth.userHasRole(targetAdmin, ROLE.SUPER_ADMIN);

            case role === ROLE.WHITELABEL_SUPER_ADMIN:
                return !Auth.userHasAnyRole(targetAdmin, [...this.SUPER_ROLES, ROLE.ADMIN]);

            case this.NON_SUPER_ROLES.includes(role):
                return !this.hasMainRole(targetAdmin);

            default:
                return false;
        }
    }

    /**
     * Gets the strongest admin role based on ROLES_ORDER
     */
    public getStrongestRole(admin: Admin): RoleType | null {
        let lowest = Number.MAX_SAFE_INTEGER;

        admin.roles.forEach((role) => {
            const index = this.ROLES_ORDER.indexOf(role.name);

            if (index !== -1) {
                lowest = Math.min(lowest, index);
            }
        });

        if (lowest < Number.MAX_SAFE_INTEGER) {
            return this.ROLES_ORDER[lowest];
        }

        return null;
    }
}

const adminPolicyService = new AdminPolicyService();

export const ROLES = {
    SUPER_ADMINS: adminPolicyService.SUPER_ROLES,
    NON_SUPER_ADMINS: adminPolicyService.NON_SUPER_ROLES,
    MAIN_ADMINS: adminPolicyService.MAIN_ROLES,
};

export default adminPolicyService;
